<template>
  <div
    class="box-border w-[280px] max-w-full min-w-0 overflow-hidden rounded-12 bg-chat-feed-messages-nested-areas-bg-inc text-label-r-13-18"
    data-test="invite-link-in-message"
  >
    <div
      class="flex items-center gap-2 pt-3 px-3 pb-2"
      :class="{ 'pb-3': !showFooterAction }"
    >
      <contact-avatar
        v-bind="room ? { room } : {}"
        :name="displayName"
        :is-room="true"
        :size="40"
        class="shrink-0"
      />
      <div class="min-w-0 grow">
        <p class="m-0 truncate text-body-m-15-22 text-fg-base-fg3">
          {{ displayName }}
        </p>
        <p
          v-if="membersLabel"
          class="m-0 truncate text-body-r-14-20 text-fg-base-fg17"
        >
          {{ membersLabel }}
        </p>
      </div>
    </div>

    <div
      v-if="showFooterAction"
      class="p-1 w-full"
    >
      <div class="h-px bg-bor-nav w-full" />
    </div>

    <div
      v-if="showFooterAction"
      class="flex w-full items-stretch px-2 pb-1.5 pt-1"
    >
      <wui-btn
        class="min-w-0 flex-1 text-body-m-14-20!"
        variant="brand"
        state="alpha"
        :size="32"
        :disabled="isJoining"
        :loading="isJoining"
        data-test="invite-link-action-button"
        @click.stop="onFooterAction"
      >
        {{ footerActionLabel }}
      </wui-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { WuiBtn } from '@wui/common-library'
import { useIM, type BriefStatusResponse } from '@wui/im'
import { computed, onMounted, ref } from 'vue'

import { ContactAvatar } from '@/entities/contact-avatar'
import { isCurrentUserRoomMember, useStoreRooms } from '@/entities/room'

import { useLocalization } from '@/shared/composables/localization'
import { getTextForDifferentCount } from '@/shared/utils/get-text-for-different-count'
import { optionalPush } from '@/shared/utils/router'
import { fromPServedUser } from '@/shared/utils/uri'
import type { InviteTokenSource } from '@/shared/utils/url'
import { getChatRoute, isSameInviteRoom } from '@/shared/utils/url'

const props = withDefaults(defineProps<{
  invite: InviteTokenSource
  currentChatPServed: string
  joinInvite?: ((invite: InviteTokenSource) => Promise<void>) | undefined
}>(), {
  joinInvite: undefined,
})

const { t, tc } = useLocalization()
const { requestBriefStatus } = useIM()
const storeRooms = useStoreRooms()

const briefStatus = ref<BriefStatusResponse | null>(null)
const isJoining = ref(false)

const inviteRoomId = computed(() => fromPServedUser(props.invite.roomPServed))

const room = computed(() =>
  storeRooms.findByPServed(props.invite.roomPServed)
  ?? storeRooms.getRoomById(inviteRoomId.value),
)

const isSameRoom = computed(() =>
  isSameInviteRoom(props.invite.roomPServed, props.currentChatPServed),
)

const isAlreadyMember = computed(() => isCurrentUserRoomMember(room.value))

const showFooterAction = computed(() => !isSameRoom.value)

const footerActionLabel = computed(() =>
  isAlreadyMember.value
    ? t('chat.goToChatAction')
    : t('chat.joinChatAction'),
)

const displayName = computed(() =>
  briefStatus.value?.name
  || room.value?.name
  || t('mainAside.chatLink'),
)

const membersLabel = computed(() => {
  const count = briefStatus.value?.count ?? room.value?.subscribers?.size
  if (!count) {
    return undefined
  }

  return getTextForDifferentCount(count, {
    forOne: tc('common.participant', 0),
    forTwo: tc('common.participant', 1),
    forFive: tc('common.participant', 2),
  })
})

const loadBriefStatus = async () => {
  if (room.value?.name && room.value.subscribers?.size) {
    return
  }

  try {
    const info = await requestBriefStatus({ contactId: props.invite.roomPServed })
    if (info) {
      briefStatus.value = info
    }
  } catch {
    // keep fallback name
  }
}

const goToInviteChat = async () => {
  const target = room.value ?? inviteRoomId.value
  await optionalPush(getChatRoute(target))
}

const onJoin = async () => {
  if (isJoining.value || !props.joinInvite) {
    return
  }

  isJoining.value = true
  try {
    await props.joinInvite(props.invite)
  } finally {
    isJoining.value = false
  }
}

const onFooterAction = async () => {
  if (!showFooterAction.value || isJoining.value) {
    return
  }

  if (isAlreadyMember.value) {
    await goToInviteChat()
    return
  }

  await onJoin()
}

onMounted(() => {
  void loadBriefStatus()
})
</script>
